import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { mdiDelete } from '@mdi/js';
import { Icon } from '@mdi/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import ButtonBase from '@mui/material/ButtonBase';
import { FormControl, InputLabel, MenuItem, Select, Slide } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { defineStyles } from '../../theme';
import { useEvent } from '../../hooks';
import NewFieldInput from './NewFieldInput';
const styles = defineStyles({
    root: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
    },
    inputs: {
        pt: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        maxHeight: 640,
        rowGap: 1,
    },
    addedInputContainer: {
        display: 'flex',
        alignItems: 'center',
        columnGap: 1,
        p: 0.5,
    },
    addedInput: {
        flexGrow: 1,
    },
    actions: {},
    normalActions: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        columnGap: 2,
        mt: 2,
    },
    addColumnActions: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        columnGap: 2,
        mt: 2,
    },
    actionsButtons: {
        display: 'flex',
        columnGap: 1,
        ml: 'auto',
    },
    deleteButton: {
        color: '#757575',
    },
    input: {
        '& strong': {
            fontWeight: 400,
        },
    },
    booleanSelect: {
        '& label:not(.Mui-focused):not(.MuiFormLabel-filled)': {
            top: -6,
        },
        '& strong': {
            fontWeight: 400,
        },
    },
});
export function ModificationsForm({ onCancel, onSubmit, defaultValues, visibleColumns, allowNewColumns, fieldOverrides, fieldPrepareData, renderExtraContent, }) {
    const actionsContainerRef = useRef(null);
    const inputsContainerRef = useRef(null);
    const { control, handleSubmit, setFocus, setValue, watch, reset } = useForm({
        defaultValues: defaultValues,
    });
    // Names of all columns that already have a standard form field rendered.
    // Used to hide prefill columns that would duplicate existing fields.
    const formFieldNames = useMemo(() => new Set(visibleColumns.map((c) => c.fieldName)), [visibleColumns]);
    // Apply late-arriving defaults (e.g. from async prepare) without overwriting user input.
    useEffect(() => {
        if (defaultValues != null) {
            reset(defaultValues, { keepDirtyValues: true });
        }
    }, [defaultValues, reset]);
    const [addedFields, setAddedFields] = useState([]);
    const [showAddInput, setAddInput] = useState(false);
    const handleAddField = useEvent((value) => {
        setValue(value, '');
        setAddedFields((prev) => [...prev, value]);
        setAddInput(false);
        setTimeout(() => {
            if (!inputsContainerRef.current)
                return;
            inputsContainerRef.current.scrollTop = inputsContainerRef.current.scrollHeight;
            setFocus(value);
        }, 0);
    });
    const handleDeleteField = useCallback((fieldKey) => {
        setAddedFields((prev) => prev.filter((v) => v !== fieldKey));
        setValue(fieldKey, undefined);
    }, [setValue]);
    return (_jsxs(Box, { component: "form", onSubmit: handleSubmit(onSubmit), sx: styles.root, children: [_jsxs(Box, { sx: styles.inputs, ref: inputsContainerRef, children: [visibleColumns.map((column) => {
                        const override = fieldOverrides?.[column.fieldKey];
                        return (_jsx(Controller, { name: column.fieldKey, control: control, rules: override?.rules, render: ({ field, fieldState }) => {
                                if (override) {
                                    return override.render({
                                        column,
                                        field,
                                        fieldState,
                                        prepareData: fieldPrepareData?.[column.fieldKey],
                                    });
                                }
                                const { ref, ...fieldData } = field;
                                if (column.dataType === 'BOOLEAN') {
                                    return (_jsxs(FormControl, { fullWidth: true, sx: styles.booleanSelect, children: [_jsx(InputLabel, { children: column.header }), _jsxs(Select, { size: "small", label: column.header, ...fieldData, value: fieldData.value ?? false, children: [_jsx(MenuItem, { value: true, children: _jsx(FormattedMessage, { id: "common.yes", defaultMessage: "Yes" }) }), _jsx(MenuItem, { value: false, children: _jsx(FormattedMessage, { id: "common.no", defaultMessage: "No" }) })] })] }));
                                }
                                if (column.dataType === 'NUMBER') {
                                    return (_jsx(TextField, { ...fieldData, inputRef: ref, size: "small", variant: "outlined", type: "number", value: fieldData.value ?? '', onChange: (e) => {
                                            const value = e.target.value;
                                            fieldData.onChange(value === '' ? null : Number(value));
                                        }, label: column.header, sx: styles.input }));
                                }
                                if (column.dataType === 'TIMESTAMP') {
                                    const dateValue = fieldData.value && typeof fieldData.value === 'string'
                                        ? new Date(fieldData.value)
                                        : fieldData.value instanceof Date
                                            ? fieldData.value
                                            : null;
                                    return (_jsx(DateTimePicker, { ...fieldData, inputRef: ref, value: dateValue, onChange: (newValue) => {
                                            fieldData.onChange(newValue ? newValue.toISOString() : null);
                                        }, slotProps: {
                                            textField: {
                                                variant: 'outlined',
                                                size: 'small',
                                                InputLabelProps: {
                                                    shrink: true,
                                                },
                                            },
                                        } }));
                                }
                                return (_jsx(TextField, { ...fieldData, inputRef: ref, inputProps: column.pattern
                                        ? {
                                            pattern: column.pattern,
                                        }
                                        : undefined, size: "small", variant: "outlined", value: fieldData.value ?? '', label: column.header, sx: styles.input, error: Boolean(fieldState.error), helperText: fieldState.error?.message }));
                            } }, column.fieldName));
                    }), addedFields.map((fieldKey) => (_jsxs(Box, { sx: styles.addedInputContainer, children: [_jsx(Controller, { name: fieldKey, control: control, render: ({ field: { ref, ...fieldData } }) => (_jsx(TextField, { focused: true, ...fieldData, inputRef: ref, size: "small", variant: "outlined", sx: styles.addedInput, value: fieldData.value ?? '', label: fieldKey })) }, fieldKey), _jsx(ButtonBase, { onClick: () => handleDeleteField(fieldKey), disableRipple: true, sx: styles.deleteButton, children: _jsx(Icon, { path: mdiDelete, size: 1 }) })] }, fieldKey)))] }), renderExtraContent?.({ watch, setValue, control, formFieldNames }), _jsxs(Box, { sx: styles.actions, ref: actionsContainerRef, children: [_jsx(Slide, { direction: "right", in: showAddInput, mountOnEnter: true, unmountOnExit: true, container: actionsContainerRef.current, children: _jsx(Box, { children: _jsx(NewFieldInput, { onAddField: handleAddField, onCancel: () => setAddInput(false) }) }) }), _jsx(Slide, { direction: "left", in: !showAddInput, appear: true, mountOnEnter: true, unmountOnExit: true, container: actionsContainerRef.current, children: _jsxs(Box, { sx: styles.normalActions, children: [allowNewColumns && (_jsx(Button, { variant: "outlined", type: "button", onClick: () => setAddInput(true), children: _jsx(FormattedMessage, { id: "batchEdit.addColumn.button", defaultMessage: "Add column" }) })), _jsxs(Box, { sx: styles.actionsButtons, children: [_jsx(Button, { variant: "outlined", type: "button", onClick: onCancel, children: _jsx(FormattedMessage, { id: "common.cancel.button", defaultMessage: "Cancel" }) }), _jsx(Button, { variant: "contained", type: "submit", children: _jsx(FormattedMessage, { id: "common.submit.button", defaultMessage: "Submit" }) })] })] }) })] })] }));
}
//# sourceMappingURL=ModificationsForm.js.map